import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import Title from "../../UI/Text/Title";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Axios from "../../../axios/Axios";
import { NODE_HOST_URL_v1 } from "../api/Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faMobileAlt, faClock, faChevronLeft, faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

const RcsClickReportDetail = () => {
  const lStorage = JSON.parse(localStorage.getItem("login"));
  const userId = lStorage.user.userId;
  const userToken = lStorage.token;

  const { templateId, fromDate, toDate } = useParams();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowWidth < 640;

  const fetchDetail = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await Axios.post(
        `${NODE_HOST_URL_v1}/click_report_summary`,
        {
          user_id: userId,
          token: userToken,
          method: "rcs_template_click_count",
          fromDate,
          toDate,
          channel: "rcs",
          templateId,
          page,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (data.success) {
        setReportData(Array.isArray(data.data) ? data.data : []);
        setCurrentPage(data.currentPage || page);
        setTotalPages(Math.max(1, data.totalPages || 1));
        setTotalCount(data.totalCount || 0);
      } else {
        setError(data.message || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, fromDate, toDate, templateId]);

  useEffect(() => {
    fetchDetail(1);
  }, [fetchDetail]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchDetail(page);
  };

  const renderPagePills = () => {
    if (totalPages <= 1) return null;
    const pills = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);

    return pills.map((p, idx) =>
      p === "..." ? (
        <span key={"e" + idx} style={{ padding: "0 6px", color: "#94a3b8", fontSize: "13px" }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          style={{
            minWidth: "34px", height: "34px",
            border: "1px solid",
            borderColor: currentPage === p ? "#4f46e5" : "#e2e8f0",
            background: currentPage === p ? "#4f46e5" : "#fff",
            color: currentPage === p ? "#fff" : "#475569",
            borderRadius: "8px", fontSize: "13px",
            fontWeight: currentPage === p ? "700" : "400",
            cursor: "pointer",
          }}
        >
          {p}
        </button>
      )
    );
  };

  const isNextDisabled = currentPage >= totalPages;
  const showPagination = !loading && reportData.length > 0;

  return (
    <DashboardLayout>
      <main className="content">
        <div className="container-fluid p-0">
          <Title isButton={false}>Click Report</Title>

          {error && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "12px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "12px", padding: "14px 18px", marginBottom: "16px",
            }}>
              <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>Something went wrong</div>
                <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
              </div>
            </div>
          )}

          <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            fontFamily: "Inter, system-ui, sans-serif",
            overflow: "hidden",
          }}>
            <div style={{
              padding: "20px 24px", borderBottom: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: "12px",
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: isMobile ? "14px" : "17px", fontWeight: "800", color: "#0f172a" }}>
                  Template: <span style={{ color: "#4f46e5" }}>{templateId}</span>
                </h3>
                <span style={{
                  display: "inline-block", marginTop: "6px",
                  padding: "2px 10px", borderRadius: "100px",
                  fontSize: "11px", fontWeight: "700",
                  background: "#fdf4ff", color: "#9333ea",
                  border: "1px solid #e9d5ff",
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>
                  rcs
                </span>
                <span style={{ marginLeft: "10px", fontSize: "12px", color: "#64748b" }}>
                  {dayjs(fromDate).format("DD/MM/YYYY")} – {dayjs(toDate).format("DD/MM/YYYY")}
                </span>
              </div>
            </div>

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0", flexDirection: "column", gap: "12px" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "28px", color: "#4f46e5" }} />
                <span style={{ color: "#64748b", fontSize: "13px" }}>Loading records…</span>
              </div>
            ) : reportData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: "14px" }}>No records found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      {["Mobile", "Type", "Text", "Template ID", "Campaign ID", "Sender ID", "Created Date", "Click Count"].map((h, i) => (
                        <th key={i} style={{
                          padding: "12px 16px", fontSize: "11px", fontWeight: "700",
                          color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                          textAlign: "left", whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "13px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{row.mobile || "—"}</span>
                          </div>
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          <span style={{
                            background: "#fdf4ff", color: "#9333ea",
                            padding: "3px 10px", borderRadius: "100px",
                            fontSize: "11px", fontWeight: "700",
                            border: "1px solid #e9d5ff",
                            textTransform: "uppercase", whiteSpace: "nowrap",
                          }}>
                            {row.type || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "13px 16px", maxWidth: "220px" }}>
                          <span style={{
                            fontSize: "12px", color: "#475569",
                            display: "-webkit-box", WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
                          }}>
                            {row.text || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: "12px", color: "#475569" }}>{row.template_id || "—"}</td>
                        <td style={{ padding: "13px 16px", fontSize: "12px", color: "#475569" }}>{row.campaign_id || "—"}</td>
                        <td style={{ padding: "13px 16px", fontSize: "12px", color: "#475569" }}>{row.sender_id || "—"}</td>
                        <td style={{ padding: "13px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
                            <span style={{ fontSize: "12px", color: "#475569", whiteSpace: "nowrap" }}>
                              {row.created_date ? dayjs(row.created_date).format("DD/MM/YYYY h:mm A") : "—"}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          <span style={{
                            background: "#eef2ff", color: "#4338ca",
                            padding: "4px 10px", borderRadius: "100px",
                            fontSize: "12px", fontWeight: "700",
                          }}>
                            {row.clickCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showPagination && (
              <div style={{
                padding: "16px 24px", borderTop: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: "10px",
              }}>
                <div>
                  <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                    Page <span style={{ color: "#4f46e5" }}>{currentPage}</span>
                    {totalPages > 1 && (
                      <span style={{ color: "#334155" }}> of <span style={{ color: "#4f46e5" }}>{totalPages}</span></span>
                    )}
                    {totalCount > 0 && (
                      <span style={{ color: "#94a3b8" }}> ({totalCount} total)</span>
                    )}
                  </span>
                </div>

                <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: isMobile ? "space-between" : "flex-end" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage === 1 ? "#f8fafc" : "#fff",
                      color: currentPage === 1 ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "10px" }} /> Prev
                  </button>

                  {isMobile ? (
                    <span style={{ fontSize: "13px", color: "#475569", fontWeight: "600" }}>
                      {currentPage} / {totalPages}
                    </span>
                  ) : (
                    renderPagePills()
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={isNextDisabled}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: isNextDisabled ? "#f8fafc" : "#fff",
                      color: isNextDisabled ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: isNextDisabled ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "10px" }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </DashboardLayout>
  );
};

export default RcsClickReportDetail;
